import { action, makeObservable, observable } from "mobx";
import { TopicConfiguration } from "../config/topic-configuration";
import { NavigationConfiguration } from "../config/navigation-configuration";
import { MapViewerConfiguration } from "../config/map-viewer-configuration";
import MatomoTracker from "@datapunt/matomo-tracker-js";
import { NavigateFunction } from "react-router";
import { Authentication } from "../core/authentication";
import { Constants } from "../globals/constants";
import { Filter } from "../globals/types";

// eslint-disable-next-line no-shadow
export enum MessageBoxType {
    Standard = "standard",
    Info = "info",
    Success = "success",
    Warning = "warning",
    Error = "error"
}

export interface MessageBoxProps {
    type?: MessageBoxType;
    title: string;
    message: string;
    subTitle?: string;
    hasProgress?: boolean;
    percent?: number;
    okButton?: boolean;
    cancelButton?: boolean;
}

export type SortConfiguration = {
    attribute: string;
    direction: "ascending" | "descending";
}

export class AppState {
    // The currently activated topic
    @observable topic: TopicConfiguration;
    @observable location: Location;
    @observable match = {};
    @observable navigate: NavigateFunction;
    @observable pageTitle: string;
    @observable topContent: JSX.Element | JSX.Element[] | null;
    @observable bottomContent: JSX.Element | null;
    // unklar wieso das declare hilft
    // ohne declare erscheint der Fehler "TypeError: Cannot call a class as a function" (object-result.tsx -> kontakt)
    @observable declare asideContent: JSX.Element | null;
    @observable breadcrumbs: NavigationConfiguration = new NavigationConfiguration();
    @observable subtopic?: TopicConfiguration;
    @observable subtopicId?: string;
    @observable subtopicLabel?: string;
    @observable objectId?: string;
    @observable objectLabel?: string;
    @observable showMap = true;
    @observable userShowMap = true;
    @observable projectCategory?: string;
    @observable projectId?: number;
    @observable areaId?: number;
    @observable zoomingRequired: boolean = true;
    @observable authentication: Authentication;
    @observable projectSelectingEnabled: boolean = true;
    @observable startupExtent: any;
    @observable filters: Map<string, Filter[]> = new Map<string, Filter[]>();
    @observable searchTexts: Map<string, string> = new Map<string, string>();
    @observable sortAttributes: Map<string, SortConfiguration> = new Map<string, SortConfiguration>();
    /**
     * If user is logged in to portal
     * is checked at application startup with IdentityManager
     */
    @observable loggedIn: boolean;
    @observable esriCredential: __esri.Credential | null;
    @observable messageBox = {
        type: MessageBoxType.Standard,
        show: false,
        title: "",
        message: "",
        subTitle: "",
        hasProgress: false,
        percent: 0,
        okButton: true,
        cancelButton: false,
    };
    mapViewerConfig: MapViewerConfiguration;
    tracker: MatomoTracker | undefined;

    constructor(mapViewerConfig: MapViewerConfiguration, tracker?: MatomoTracker) {
        makeObservable(this);
        this.mapViewerConfig = mapViewerConfig;
        this.tracker = tracker;
    }

    @action setTopic(topic: TopicConfiguration): void {
        this.topic = topic;
    }

    @action
    setShowMap(show: boolean): void {
        this.showMap = show;
    }

    @action
    setUserShowMap(show: boolean) {
        this.userShowMap = show;
    }

    @action
    setProjectId(projectCategory: string, projectId: number, zoomingRequired: boolean = true): void {
        if (this.projectCategory !== projectCategory || this.projectId !== projectId || this.areaId !== undefined || this.zoomingRequired !== zoomingRequired) {
            this.projectCategory = projectCategory;
            this.projectId = projectId;
            this.areaId = undefined;
            this.zoomingRequired = zoomingRequired;
        }

        this.setShowMap(true);
    }

    @action
    clearProjectId(): void {
        this.projectCategory = undefined;
        this.projectId = undefined;
        this.areaId = undefined;
        this.zoomingRequired = false;
    }

    @action
    setAreaId(projectCategory: string, projectId: number, areaId: number): void {
        if (this.projectCategory !== projectCategory || this.projectId !== projectId || this.areaId !== areaId) {
            this.projectCategory = projectCategory;
            this.projectId = projectId;
            this.areaId = areaId;
        }

        this.setShowMap(true);
    }

    @action
    getProjectAreaWhereClause(): string {
        let whereClause = `PROJECT_CATEGORY = '${this.projectCategory}'`;

        if (this.projectId) {
            if (this.projectCategory === Constants.projectCategoryUsage) {
                whereClause += ` AND BEANSPR_ID = ${this.projectId}`;
            } else {
                whereClause += ` AND SCHAFFPR_ID = ${this.projectId}`;
            }

            if (this.areaId) {
                whereClause += ` AND FL_NR = ${this.areaId}`;
            }

            return whereClause;
        }

        throw new Error("No project id set");
    }

    @action setRoutePath(pathname: string): void {
        if (this.location.pathname !== pathname) {
            this.navigate(pathname);
            this.location.pathname = pathname;
            if (this.tracker !== undefined) {
                // TODO: Timeout durch zuverlässigere Lösung ersetzten
                setTimeout(() => {
                    if (this.tracker !== undefined) {
                        this.tracker.trackPageView();
                    }
                }, 200);
            }
        }
    }

    @action setSubtopic(subtopic?: TopicConfiguration | null): void {
        if (subtopic) {
            subtopic.revision = Math.random().toString(); // Neues Subtopic erzwingen
            this.subtopic = subtopic;
            this.subtopicId = subtopic.id;
            this.subtopicLabel = subtopic.label ? subtopic.label : subtopic.name;
        } else {
            this.subtopic = undefined;
            this.subtopicId = undefined;
            this.subtopicLabel = undefined;
        }
    }

    /**
     * Show a message box
     *
     * @param props MessageBoxProps
     */
    @action showMessageBox(props: MessageBoxProps): void {
        this.messageBox.show = true;

        const { title, message, subTitle, hasProgress, percent } = props;
        this.messageBox.title = title;
        this.messageBox.message = message;
        if (subTitle) {
            this.messageBox.subTitle = subTitle;
        }
        this.messageBox.hasProgress = hasProgress || false;
        if (percent) {
            this.messageBox.percent = percent;
        }

        if (props.hasOwnProperty("type") && props.type !== undefined) {
            this.messageBox.type = props.type;
        }
        if (props.hasOwnProperty("okButton") && props.okButton !== undefined) {
            this.messageBox.okButton = props.okButton;
        }
        if (props.hasOwnProperty("cancelButton") && props.cancelButton !== undefined) {
            this.messageBox.cancelButton = props.cancelButton;
        }
    }

    /**
     * Close the message box
     */
    @action closeMessageBox(): void {
        this.messageBox.type = MessageBoxType.Standard;
        this.messageBox.show = false;
        this.messageBox.title = "";
        this.messageBox.message = "";
        this.messageBox.subTitle = "";
        this.messageBox.hasProgress = false;
        this.messageBox.percent = 0;
        this.messageBox.okButton = true;
        this.messageBox.cancelButton = false;
    }

    /**
     * Update progress on message box
     * Use 'hasProgress = true' to show progress bar on message box
     *
     * @param percent
     */
    @action progressMessageBox(percent: number): void {
        if (percent < 0) {
            percent = 0;
        }
        if (percent > 100) {
            percent = 100;
        }
        this.messageBox.percent = percent;
    }

    @action
    addFilter(key: string, filter: Filter): void {
        if (!this.filters.has(key)) {
            this.filters.set(key, []);
        }

        const filters = this.filters.get(key)!;
        filters.push(filter);
    }

    @action
    removeFilter(key: string, filter: Filter): boolean {
        const filters = this.filters.get(key);

        if (filters !== undefined) {
            const newFilters = filters.filter(f => f.id !== filter.id);
            this.filters.set(key, newFilters);
            return true;
        }

        return false;
    }

    @action
    setSearchText(key: string, text: string): void {
        this.searchTexts.set(key, text);
    }

    @action
    clearSearchText(key: string): boolean {
        return this.searchTexts.delete(key);
    }

    @action
    setSortConfiguration(key: string, sortConfiguration: SortConfiguration): void {
        this.sortAttributes.set(key, sortConfiguration);
    }
}
